import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { db } from "@/integrations/neon/db";
import { ProgramItem, ProgramAIData } from "@/hooks/program/types";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { PROGRAM_GENERATION_CREDITS } from "@/constants/programGenerationCredits";
import { ensureProgramCredits, invalidateProgramCreditQueries } from "@/utils/programCreditsGeneration";
import { programRealTimeService } from "@/services/realtime/ProgramRealTimeService";
import { invokeApi } from "@/utils/supabaseEdgeFunction";
import {
  isMagazineGenerating,
  isResearchGenerating,
  isSocialBatchGenerating,
} from "@/utils/programAiGenerationUi";
import { incrementMagazineGenerations } from "@/hooks/analytics/incrementMagazineGenerations";
import { incrementResearchGenerations } from "@/hooks/analytics/incrementResearchGenerations";
import { incrementSocialGenerations } from "@/hooks/analytics/incrementSocialGenerations";

interface ProgramDataContextProps {
  programData: ProgramItem | null;
  programAIData: ProgramAIData | null;
  isLoadingProgram: boolean;
  isLoadingAIData: boolean;
  isProgramNotFound: boolean;
  isArticleGenerating: boolean;
  isMagazineArticleGenerating: boolean;
  isSocialPostsGenerating: boolean;
  error: string | null;
  fetchProgramData: (programId: string) => Promise<ProgramItem | null>;
  refreshProgramData: (programId: string) => Promise<ProgramItem | null>;
  refreshProgramAIData: (opts?: {
    silent?: boolean;
    /** When set, refresh this program's AI row (avoids relying on internal refs during transcribe polling). */
    programId?: string;
  }) => Promise<ProgramAIData | null>;
  generateArticle: (options?: {
    extractModelOverride?: string;
    agentModelOverride?: string;
  }) => Promise<boolean>;
  generateMagazineArticle: (isPodcast?: boolean) => Promise<boolean>;
  generateSocialPosts: (options?: { modelOverride?: string }) => Promise<boolean>;
  resetSocialPosts: () => Promise<boolean>;
  saveProgramAIData: (programId: string, aiData: Partial<ProgramAIData>) => Promise<boolean>;
  clearProgramData: () => void;
  clearProgramAIData: () => void;
  setProgramAIData: React.Dispatch<React.SetStateAction<ProgramAIData | null>>;
}

/** Module-private: exporting this breaks Vite React Fast Refresh alongside Provider/hook. */
const ProgramDataContext = createContext<ProgramDataContextProps>({
  programData: null,
  programAIData: null,
  isLoadingProgram: false,
  isLoadingAIData: false,
  isProgramNotFound: false,
  isArticleGenerating: false,
  isMagazineArticleGenerating: false,
  isSocialPostsGenerating: false,
  error: null,
  fetchProgramData: async () => null,
  refreshProgramData: async () => null,
  refreshProgramAIData: async (_opts?: {
    silent?: boolean;
    programId?: string;
  }) => null,
  generateArticle: async () => false,
  generateMagazineArticle: async () => false,
  generateSocialPosts: async () => false,
  resetSocialPosts: async () => false,
  saveProgramAIData: async () => false,
  clearProgramData: () => {},
  clearProgramAIData: () => {},
  setProgramAIData: () => {}
});

export const ProgramDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, user } = useAuth();
  const queryClient = useQueryClient();
  const [programData, setProgramData] = useState<ProgramItem | null>(null);
  const [programAIData, setProgramAIData] = useState<ProgramAIData | null>(null);
  const [isLoadingProgram, setIsLoadingProgram] = useState(false);
  const [isLoadingAIData, setIsLoadingAIData] = useState(false);
  const [isProgramNotFound, setIsProgramNotFound] = useState(false);
  const [isArticleGenerating, setIsArticleGenerating] = useState(false);
  const [isMagazineArticleGenerating, setIsMagazineArticleGenerating] = useState(false);
  const [isSocialPostsGenerating, setIsSocialPostsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const unmountedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const latestUpdateTimestampRef = useRef<string | null>(null);
  const currentProgramIdRef = useRef<string | null>(null);
  const diagnosticsRanForProgramRef = useRef<string | null>(null);
  const userIdRef = useRef<string | undefined>(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);
  
  useEffect(() => {
    const programId = programData?.id;
    if (!programId || unmountedRef.current) return;

    currentProgramIdRef.current = programId;

    const unsubscribe = programRealTimeService.subscribeToProgram(programId, {
      onUpdate: (updatedData) => {
        if (unmountedRef.current) return;

        if (latestUpdateTimestampRef.current && 
            updatedData.updated_at && 
            new Date(latestUpdateTimestampRef.current) > new Date(updatedData.updated_at)) {
          return;
        }
        
        if (updatedData.updated_at) {
          latestUpdateTimestampRef.current = updatedData.updated_at;
        }
        
        setProgramAIData(updatedData);
        
        if (isResearchGenerating(updatedData)) {
          setIsArticleGenerating(true);
        } else {
          setIsArticleGenerating(false);
        }
        
        if (isMagazineGenerating(updatedData)) {
          setIsMagazineArticleGenerating(true);
        } else {
          setIsMagazineArticleGenerating(false);
        }
        
        setIsSocialPostsGenerating(isSocialBatchGenerating(updatedData));
      },
      onError: (errorMsg) => {
        console.error(`Real-time subscription error for program ${programId}:`, errorMsg);
        if (!unmountedRef.current) {
          setError(errorMsg);
        }
      },
      onArticleGenerated: () => {
        if (!unmountedRef.current) {
          setIsArticleGenerating(false);
        }
      },
      onMagazineArticleGenerated: () => {
        if (!unmountedRef.current) {
          setIsMagazineArticleGenerating(false);
        }
      },
      onSocialPostsGenerated: () => {
        if (!unmountedRef.current) {
          setIsSocialPostsGenerating(false);
        }
      },
      // Background jobs charge credits server-side just before clearing their flag.
      onGenerationFinished: () => {
        invalidateProgramCreditQueries(queryClient, userIdRef.current);
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [programData?.id]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      currentProgramIdRef.current = null;
    };
  }, []);

  const fetchProgramData = useCallback(async (programId: string) => {
    if (!session || isFetchingRef.current || unmountedRef.current) return null;
    
    isFetchingRef.current = true;
    currentProgramIdRef.current = programId;
    
    try {
      setIsLoadingProgram(true);
      setError(null);
      setIsProgramNotFound(false);
      
      
      const { data, error } = await db
        .from('program_items')
        .select('*')
        .eq('id', programId)
        .single();
      
      if (error) {
        console.error('Error fetching program:', error);
        setError(`Failed to load program: ${error.message}`);
        if (error.code === "PGRST116") {
          setIsProgramNotFound(true);
        }
        return null;
      }
      
      if (unmountedRef.current) {
        console.log('Component unmounted during fetch, skipping update');
        return data;
      }
      
      setProgramData(data);
      
      if (data) {
        await refreshProgramAIData();
      }
      
      return data;
    } catch (err: any) {
      console.error('Error in fetchProgramData:', err);
      setError(err.message || 'Failed to load program');
      return null;
    } finally {
      isFetchingRef.current = false;
      if (!unmountedRef.current) {
        setIsLoadingProgram(false);
      }
    }
  }, [session]);

  const refreshProgramData = useCallback(async (programId: string) => {
    if (!session || unmountedRef.current) return null;
    
    try {
      setIsLoadingProgram(true);
      setError(null);
      setIsProgramNotFound(false);
      console.log(`Refreshing program data for ID: ${programId}`);
      
      const { data, error } = await db
        .from('program_items')
        .select('*')
        .eq('id', programId)
        .single();
      
      if (error) {
        console.error('Error refreshing program:', error);
        setError(`Failed to refresh program: ${error.message}`);
        if (error.code === "PGRST116") {
          setIsProgramNotFound(true);
        }
        return null;
      }
      
      if (unmountedRef.current) {
        console.log('Component unmounted during refresh, skipping update');
        return data;
      }
      
      setProgramData(data);
      return data;
    } catch (err: any) {
      console.error('Error in refreshProgramData:', err);
      setError(err.message || 'Failed to refresh program');
      return null;
    } finally {
      if (!unmountedRef.current) {
        setIsLoadingProgram(false);
      }
    }
  }, [session]);

  const refreshProgramAIData = useCallback(async (opts?: {
    silent?: boolean;
    programId?: string;
  }): Promise<ProgramAIData | null> => {
    const programId = opts?.programId ?? currentProgramIdRef.current;
    if (!session || !programId || unmountedRef.current) {
      console.log("Cannot refresh AI data: missing session, program ID, or component unmounted", {
        hasSession: !!session,
        programId,
        unmounted: unmountedRef.current
      });
      return null;
    }
    
    try {
      
      setError(null);
      if (!opts?.silent) {
        setIsLoadingAIData(true);
      }
      
      const { data, error } = await db
        .from('program_ai_data')
        .select('*')
        .eq('program_id', programId)
        .maybeSingle();
      
      if (error) {
        console.error('Error refreshing program AI data:', error);
        if (!unmountedRef.current) {
          setError(`Failed to refresh AI data: ${error.message}`);
          if (!opts?.silent) {
            setIsLoadingAIData(false);
          }
        }
        return null;
      }
      
      if (!data) {
        // Set loading to false since there's no data to load
        if (!unmountedRef.current && !opts?.silent) {
          setIsLoadingAIData(false);
        }
        return null;
      }
      
      if (unmountedRef.current) {
        return data;
      }
      
      if (data?.updated_at) {
        latestUpdateTimestampRef.current = data.updated_at;
      }
      
      if (isResearchGenerating(data)) {
        setIsArticleGenerating(true);
      } else {
        setIsArticleGenerating(false);
      }
      
      if (isMagazineGenerating(data)) {
        setIsMagazineArticleGenerating(true);
      } else {
        setIsMagazineArticleGenerating(false);
      }

      setIsSocialPostsGenerating(isSocialBatchGenerating(data));
      
      setProgramAIData(data);
      if (!opts?.silent) {
        setIsLoadingAIData(false);
      }
      
      return data;
    } catch (err: any) {
      console.error('Error in refreshProgramAIData:', err);
      if (!unmountedRef.current) {
        setError(err.message || 'Failed to refresh AI data');
        if (!opts?.silent) {
          setIsLoadingAIData(false);
        }
      }
      return null;
    }
  }, [session]);

  const generateArticle = useCallback(async (options?: {
    extractModelOverride?: string;
    agentModelOverride?: string;
  }): Promise<boolean> => {
    if (!session) {
      toast.error("You must be logged in to generate an article");
      return false;
    }
    
    if (!currentProgramIdRef.current || !programAIData || unmountedRef.current) {
      console.log("Cannot generate article: missing program ID, AI data, or component unmounted");
      return false;
    }
    
    if (!programAIData.transcript && !programAIData.ai_edited_transcript) {
      toast.error("Transcript is required to generate an article");
      return false;
    }

    const uid = user?.id;
    const researchCost = PROGRAM_GENERATION_CREDITS.researchArticle;
    if (!uid || !(await ensureProgramCredits(uid, researchCost, "research article"))) {
      return false;
    }

    const programId = currentProgramIdRef.current;
    try {
      if (!unmountedRef.current) {
        setIsArticleGenerating(true);
      }

      const { data, error } = await invokeApi<{ success?: boolean }>(
        "program-ai-generate-research",
        {
          method: "POST",
          body: {
            programId,
            extractModelOverride: options?.extractModelOverride,
            agentModelOverride: options?.agentModelOverride,
          },
        }
      );

      if (error || !data?.success) {
        if (!unmountedRef.current) {
          setIsArticleGenerating(false);
          setError(error?.message || "Research article generation failed");
        }
        toast.error(error?.message || "Research article generation failed");
        return false;
      }

      // The server runs the job in the background, charges credits when it succeeds, and
      // clears `generating_research_article`; the realtime poll picks up the result.
      if (user?.id) {
        await incrementResearchGenerations(user.id);
      }
      if (!unmountedRef.current) {
        await refreshProgramAIData({ silent: true, programId });
      }
      return true;
    } catch (err: unknown) {
      console.error("Error generating article:", err);
      if (!unmountedRef.current) {
        setIsArticleGenerating(false);
        setError(err instanceof Error ? err.message : "Failed to generate article");
      }
      toast.error("Failed to generate article");
      return false;
    }
  }, [session, programData, programAIData, user?.id, queryClient, refreshProgramAIData]);

  const generateMagazineArticle = useCallback(async (
    isPodcast?: boolean
  ): Promise<boolean> => {
    if (!session) {
      toast.error("You must be logged in to generate a magazine article");
      return false;
    }
    
    if (!currentProgramIdRef.current || !programAIData || unmountedRef.current) {
      console.log("Cannot generate magazine article: missing program ID, AI data, or component unmounted");
      return false;
    }
    
    if (!programAIData.transcript && !programAIData.ai_edited_transcript) {
      toast.error("Transcript is required to generate a magazine article");
      return false;
    }

    const uid = user?.id;
    const magazineCost = PROGRAM_GENERATION_CREDITS.magazineArticle;
    if (!uid || !(await ensureProgramCredits(uid, magazineCost, "magazine article"))) {
      return false;
    }

    const contentType = isPodcast ? "podcast" : "magazine";
    const programId = currentProgramIdRef.current;

    try {
      if (!unmountedRef.current) {
        setIsMagazineArticleGenerating(true);
      }

      const { data, error } = await invokeApi<{ success?: boolean }>(
        "program-ai-generate-magazine",
        {
          method: "POST",
          body: { programId },
        }
      );

      if (error || !data?.success) {
        if (!unmountedRef.current) {
          setIsMagazineArticleGenerating(false);
          setError(error?.message || "Magazine article generation failed");
        }
        toast.error(error?.message || `Failed to generate ${contentType} article`);
        return false;
      }

      // Runs in the background on the server, which charges credits on success.
      if (user?.id) {
        await incrementMagazineGenerations(user.id);
      }
      if (!unmountedRef.current) {
        await refreshProgramAIData({ silent: true, programId });
      }
      return true;
    } catch (err: unknown) {
      console.error("Error generating magazine article:", err);
      if (!unmountedRef.current) {
        setIsMagazineArticleGenerating(false);
        setError(err instanceof Error ? err.message : "Failed to generate magazine article");
      }
      toast.error("Failed to generate magazine article");
      return false;
    }
  }, [session, programData, programAIData, user?.id, queryClient, refreshProgramAIData]);

  const generateSocialPosts = useCallback(async (options?: {
    modelOverride?: string;
  }): Promise<boolean> => {
    if (!session) {
      toast.error("You must be logged in to generate social media posts");
      return false;
    }
    
    if (!currentProgramIdRef.current || !programAIData || unmountedRef.current) {
      console.log("[ProgramDataContext] Cannot generate social posts: missing program ID, AI data, or component unmounted", {
        programId: currentProgramIdRef.current,
        hasAIData: !!programAIData,
        unmounted: unmountedRef.current
      });
      return false;
    }
    
    if (!programAIData.transcript && !programAIData.ai_edited_transcript) {
      toast.error("Transcript is required to generate social media posts");
      return false;
    }

    const uid = user?.id;
    const socialCost = PROGRAM_GENERATION_CREDITS.socialPosts;
    if (!uid || !(await ensureProgramCredits(uid, socialCost, "social media posts"))) {
      return false;
    }

    const programId = currentProgramIdRef.current;

    try {
      if (!unmountedRef.current) {
        setIsSocialPostsGenerating(true);
      }

      const { data, error } = await invokeApi<{ success?: boolean }>(
        "program-ai-generate-social",
        {
          method: "POST",
          body: {
            programId,
            all: true,
            enable_emojis: true,
            modelOverride: options?.modelOverride,
          },
        }
      );

      if (error || !data?.success) {
        if (!unmountedRef.current) {
          setIsSocialPostsGenerating(false);
          setError(error?.message || "Social posts generation failed");
        }
        toast.error(error?.message || "Failed to generate social posts");
        return false;
      }

      // Runs in the background on the server, which charges credits on success.
      if (user?.id) {
        await incrementSocialGenerations(user.id);
      }
      if (!unmountedRef.current) {
        await refreshProgramAIData({ silent: true, programId });
      }
      return true;
    } catch (err: unknown) {
      console.error("[ProgramDataContext] Error generating social posts:", err);
      console.log("[ProgramDataContext] Error details:", typeof err, err.stack || "No stack trace available");
      if (!unmountedRef.current) {
        setIsSocialPostsGenerating(false);
        setError(err instanceof Error ? err.message : "Failed to generate social posts");
      }
      toast.error("Failed to generate social posts");
      return false;
    }
  }, [session, programData, programAIData, user?.id, queryClient, refreshProgramAIData]);

  const resetSocialPosts = useCallback(async (): Promise<boolean> => {
    if (!session) {
      toast.error("You must be logged in to reset social media posts");
      return false;
    }
    
    if (!currentProgramIdRef.current || unmountedRef.current) {
      console.log("[ProgramDataContext] Cannot reset social posts: missing program ID or component unmounted", {
        programId: currentProgramIdRef.current,
        unmounted: unmountedRef.current
      });
      return false;
    }
    
    try {
      console.log("[ProgramDataContext] Resetting social posts for program ID:", currentProgramIdRef.current);
      
      const resetData = {
        ai_linkedin_post: null,
        ai_instagram_post: null,
        ai_x_post: null,
        ai_threads_post: null,
        ai_facebook_post: null,
        generating_social_media_posts: false,
        generating_social_platform: null,
      };
      
      if (!unmountedRef.current) {
        setIsSocialPostsGenerating(false);
        setProgramAIData(prevData => 
          prevData ? {
            ...prevData,
            ...resetData
          } : null
        );
      }
      
      console.log("[ProgramDataContext] Executing database update to clear social posts");
      const { error: updateError, data: updateResult } = await db
        .from('program_ai_data')
        .update(resetData)
        .eq('program_id', currentProgramIdRef.current)
        .select();
      
      if (updateError) {
        console.error("[ProgramDataContext] Error resetting social posts in database:", updateError);
        console.log("[ProgramDataContext] Database error details:", updateError);
        toast.error("Failed to reset social posts in database");
        return false;
      }
      
      console.log("[ProgramDataContext] Database reset successful:", updateResult);
      
      await refreshProgramAIData();
      
      return true;
    } catch (err: any) {
      console.error("[ProgramDataContext] Error in resetSocialPosts:", err);
      console.log("[ProgramDataContext] Error details:", typeof err, err.stack || "No stack trace available");
      toast.error(err.message || "Failed to reset social posts");
      return false;
    }
  }, [session, refreshProgramAIData]);

  const saveProgramAIData = useCallback(async (programId: string, aiData: Partial<ProgramAIData>): Promise<boolean> => {
    if (!session) {
      toast.error("You must be logged in to save AI data");
      return false;
    }
    
    if (unmountedRef.current) {
      console.log('Component unmounted, skipping save operation');
      return false;
    }
    
    try {
      setIsLoadingAIData(true);
      setError(null);
      console.log(`Saving AI data for program ${programId}:`, aiData);
      
      const { data: existingData, error: checkError } = await db
        .from('program_ai_data')
        .select('id, updated_at, user_id, owner_team_id')
        .eq('program_id', programId)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking existing program AI data:', checkError);
        setError(`Failed to save AI data: ${checkError.message}`);
        toast.error("Failed to save AI data");
        return false;
      }
      
      let result;
      const timestamp = new Date().toISOString();
      
      const { data: programData } = await db
        .from('program_items')
        .select('user_id, owner_team_id')
        .eq('id', programId)
        .maybeSingle();
      
      const userId = existingData?.user_id || programData?.user_id || session?.user?.id || null;
      const ownerTeamId = existingData?.owner_team_id || programData?.owner_team_id || null;
      
      console.log(`Using owner_team_id and user_id for program AI data`);
      
      if (existingData) {
        const { data, error } = await db
          .from('program_ai_data')
          .update({
            ...aiData,
            updated_at: timestamp,
            user_id: userId,
            owner_team_id: ownerTeamId
          })
          .eq('program_id', programId)
          .select()
          .single();
        
        if (error) {
          console.error('Error updating program AI data:', error);
          setError(`Failed to update AI data: ${error.message}`);
          toast.error("Failed to update AI data");
          return false;
        }
        
        result = data;
      } else {
        const { data, error } = await db
          .from('program_ai_data')
          .insert({
            program_id: programId,
            ...aiData,
            user_id: userId,
            owner_team_id: ownerTeamId,
            created_at: timestamp,
            updated_at: timestamp
          })
          .select()
          .single();
        
        if (error) {
          console.error('Error inserting program AI data:', error);
          setError(`Failed to save AI data: ${error.message}`);
          toast.error("Failed to save AI data");
          return false;
        }
        
        result = data;
      }
      
      if (unmountedRef.current) {
        console.log('Component unmounted during save, skipping state update');
        return true;
      }
      
      console.log('Program AI data saved successfully:', result);
      
      if (result?.updated_at) {
        latestUpdateTimestampRef.current = result.updated_at;
      }
      
      setProgramAIData(result);
      
      return true;
    } catch (err: any) {
      console.error('Error in saveProgramAIData:', err);
      setError(err.message || 'Failed to save AI data');
      toast.error("Failed to save AI data");
      return false;
    } finally {
      if (!unmountedRef.current) {
        setIsLoadingAIData(false);
      }
    }
  }, [session]);

  const clearProgramData = useCallback(() => {
    currentProgramIdRef.current = null;
    latestUpdateTimestampRef.current = null;
    setProgramData(null);
    setProgramAIData(null);
    setIsLoadingProgram(false);
    setIsLoadingAIData(false);
    setIsProgramNotFound(false);
    setError(null);
    setIsArticleGenerating(false);
    setIsMagazineArticleGenerating(false);
    setIsSocialPostsGenerating(false);
  }, []);

  const clearProgramAIData = useCallback(() => {
    setProgramAIData(null);
  }, []);

  return (
    <ProgramDataContext.Provider
      value={{
        programData,
        programAIData,
        isLoadingProgram,
        isLoadingAIData,
        isProgramNotFound,
        isArticleGenerating,
        isMagazineArticleGenerating,
        isSocialPostsGenerating,
        error,
        fetchProgramData,
        refreshProgramData,
        refreshProgramAIData,
        generateArticle,
        generateMagazineArticle,
        generateSocialPosts,
        resetSocialPosts,
        saveProgramAIData,
        clearProgramData,
        clearProgramAIData,
        setProgramAIData
      }}
    >
      {children}
    </ProgramDataContext.Provider>
  );
};

export const useProgramData = () => useContext(ProgramDataContext);
